import navTree from "@stanza/shared/nav-tree.json";
import { getPageRoute } from "src/services";

/**
 * The browse navigation tree: top-level categories, each with nested tag nodes.
 * Sourced from the shared `nav-tree.json` so the whole nav is data-driven. A
 * top-level node is a category; a nested node is a tag unless it carries
 * `group: true` (a pure expandable container that routes nowhere).
 */
export const NAV_CATEGORIES = navTree.categories;

/**
 * Top-level category ("sports" | "tv") for each league/network/genre slug,
 * built from the nav tree. Movie genres are omitted because they overlap TV
 * genre slugs; a movie calendar is detected by its explicit `movies` tag.
 */
const TAG_CATEGORY = (() => {
  const map = {};
  const walk = (nodes, categoryTag) => {
    for (const node of nodes) {
      if (node.tag) map[node.tag] = categoryTag;
      if (node.children) walk(node.children, categoryTag);
    }
  };
  for (const category of NAV_CATEGORIES) {
    if (category.tag === "sports" || category.tag === "tv") walk(category.children ?? [], category.tag);
  }
  return map;
})();

/**
 * A node's kind, which decides how it routes and renders.
 *
 * @param {object} node - A nav-tree node.
 * @param {boolean} isCategory - Whether the node is a top-level category.
 * @returns {"category"|"group"|"coming-soon"|"tag"}
 */
export function nodeKind(node, isCategory) {
  if (node.comingSoon) return "coming-soon";
  if (isCategory) return "category";
  if (node.group) return "group";
  return "tag";
}

/**
 * Whether a node expands to reveal children.
 *
 * @param {object} node - A nav-tree node.
 * @returns {boolean}
 */
export function isExpandable(node) {
  return Array.isArray(node.children) && node.children.length > 0;
}

/**
 * The route a node navigates to, or null for nodes that only expand. Categories
 * are expand-only (their landing pages aren't ready) — they reveal their tags
 * but don't navigate. Tags hit the tag page (`/tag/:slug`).
 *
 * @param {object} node - A nav-tree node.
 * @param {boolean} isCategory - Whether the node is a top-level category.
 * @param {object} [category] - The top-level category this node lives under;
 *   its genre children route to the film-only genre page rather than a tag page.
 * @returns {string|null}
 */
export function routeForNode(node, isCategory, category) {
  if (isCategory) return null;
  if (node.group) return null;
  if (node.comingSoon) return null;
  if (category?.tag === "movies") return getPageRoute("home", "MOVIE_GENRE", { genre: node.tag });
  return getPageRoute("home", "TAG", { tag: node.tag });
}

/**
 * Display label for a movie-genre slug, sourced from the Movies category's nav
 * children so the label lives in one place.
 *
 * @param {string} genre - A movie-genre slug (e.g. "scifi").
 * @returns {string} The label, or the slug itself when unknown.
 */
export function movieGenreLabel(genre) {
  const movies = NAV_CATEGORIES.find((category) => category.tag === "movies");
  return (movies?.children ?? []).find((node) => node.tag === genre)?.label ?? genre;
}

/**
 * Every tag slug in the tree (leaves and tag containers), for validation/tests.
 *
 * @returns {string[]}
 */
export function allTagSlugs() {
  const out = [];
  const walk = (nodes, category) => {
    for (const node of nodes) {
      // Movie genres route to the film-only genre page, not a tag page, so they
      // are excluded here the same as groups and coming-soon teasers.
      if (!node.group && !node.comingSoon && category?.tag !== "movies") out.push(node.tag);
      if (node.children) walk(node.children, category);
    }
  };
  NAV_CATEGORIES.forEach((category) => category.children && walk(category.children, category));
  return out;
}

/**
 * Classifies a calendar into a top-level library category from its tags.
 *
 * @param {string[]} tags - Calendar tag slugs.
 * @returns {"sports"|"tv"|"movies"|null} The category, or null when unknown.
 */
export function categoryForTags(tags) {
  if (!Array.isArray(tags)) return null;
  const norm = tags.map((tag) => String(tag).toLowerCase());
  if (norm.includes("movies")) return "movies";
  if (norm.some((tag) => TAG_CATEGORY[tag] === "sports")) return "sports";
  if (norm.some((tag) => TAG_CATEGORY[tag] === "tv")) return "tv";
  return null;
}
